import { Dispatch, FC, Fragment, SetStateAction, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api';
import { googleMapConfig } from 'src/app-config';
import { HTHInteractiveMapMapActivity } from './HTHInteractiveMapMapActivity';
import { HTHInteractiveMapMapBlock } from './HTHInteractiveMapMapBlock';
import { HTHInteractiveMapMapBuildings } from './HTHInteractiveMapMapBuildings';
import { HTHInteractiveMapMapNumber } from './HTHInteractiveMapMapNumber';
import { HTHInteractiveMapSelectionMode } from './HTHInteractiveMapSelectionMode';
import { HTHInteractiveMapSelectors } from './HTHInteractiveMapSelectors';
import { Loading } from 'src/components/_commons';
import { TerritoryDates } from 'src/custom-hooks/useTerritoryDates';
import { typeHTHTerritory, typeMapDoc, typeMapFamilia, typeMapToast, typePublicMinistryDoc } from '@monorepo/models';
import { useGetConfig, useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    hthTerritories: typeHTHTerritory[];
    mapDoc: typeMapDoc;
    publicMinistryDoc: typePublicMinistryDoc;
    setToast: Dispatch<SetStateAction<typeMapToast | null>>;
    territoryDates: TerritoryDates;
};

export const HTHInteractiveMapMap: FC<propsType> = ({
    hthTerritories, mapDoc, publicMinistryDoc, setToast, territoryDates
}) => {
    const [selectedFamilia, setSelectedFamilia] = useState<typeMapFamilia | null>(null);
    const [selectedSelection, setSelectedSelection] = useState<string>('');
    const [selectedTerritories, setSelectedTerritories] = useState<string[]>([]);
    const [selectionMode, setSelectionMode] = useState(false);
    const [showActivity, setShowActivity] = useState(false);
    const [showBuildings, setShowBuildings] = useState(false);
    const [showDates, setShowDates] = useState(false);
    const [showFamilies, setShowFamilies] = useState(true);
    const [showNumbers, setShowNumbers] = useState(true);
    const config = useGetConfig();
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();
    const map = useRef<any>(null);
    const mapLoader = useJsApiLoader(googleMapConfig);

    const clearSelection = () => {
        setSelectedTerritories([]);
        setSelectedSelection('');
    }

    // const openModalHandler = (territoryNumber: string) => {
    //     dispatch(setValuesAndOpenAlertModalReducer({
    //         mode: 'confirm',
    //         title: "Confirmar",
    //         message: `¿Ir al territorio ${territoryNumber}?`,
    //         execution: () => {
    //             navigate(`/casa-en-casa/${territoryNumber}`);
    //         }
    //     }));
    // }

    const extractInitials = (familia: string) => {
        return familia.split(' ').map(word => word[0]).join('');
    }

    const handleSelectTerritory = (territoryNumber: string): void => {
        if (!selectionMode) {
            return;
        }
        setSelectedTerritories(prev =>
            prev.includes(territoryNumber) ?
                prev.filter(n => n !== territoryNumber)
                :
                [...prev, territoryNumber]
        );
    }

    if (!mapLoader.isLoaded) {
        return <Loading mt={'80px'} />;
    }

    return (
        <>
        <HTHInteractiveMapSelectors
            clearSelection={clearSelection}
            selectionMode={selectionMode}
            setSelectionMode={setSelectionMode}
            setShowActivity={setShowActivity}
            setShowBuildings={setShowBuildings}
            setShowDates={setShowDates}
            setShowFamilies={setShowFamilies}
            setShowNumbers={setShowNumbers}
            showActivity={showActivity}
            showBuildings={showBuildings}
            showDates={showDates}
            showFamilies={showFamilies}
            showNumbers={showNumbers}
            territoryDates={territoryDates}
        />  
        {selectionMode &&
            <HTHInteractiveMapSelectionMode
                clearSelection={clearSelection}
                hthTerritories={hthTerritories}
                mapDoc={mapDoc}
                selectedSelection={selectedSelection}
                selectedTerritories={selectedTerritories}
                setSelectedSelection={setSelectedSelection}
                setSelectedTerritories={setSelectedTerritories}
                setSelectionMode={setSelectionMode}
                setToast={setToast}
            />
        }
        <div className={`${isDarkMode ? 'text-dark' : ''} mt-3`}>
            <GoogleMap
                center={config.centralPointCoordinates ? config.centralPointCoordinates : { lat: 0, lng: 0 }}
                id={googleMapConfig.id}
                mapContainerClassName={'d-block m-auto'}
                mapContainerStyle={{
                    border: isDarkMode ? '1px solid #495057' : '1px solid #dee2e6',
                    borderRadius: '12px',
                    height: isMobile ? '600px' : '530px',
                    width: '100%'
                }}
                onLoad={mapInstance => {
                    map.current = mapInstance;
                    mapInstance.setZoom(16);
                }}
                options={{
                    disableDefaultUI: false,
                    fullscreenControl: true,
                    gestureHandling: 'greedy',
                    mapTypeControl: false,
                    rotateControl: true,
                    streetViewControl: false,
                    zoomControl: true
                }}
            >
                {hthTerritories.map(t =>
                    <Fragment key={t.number}>
                        {showActivity ?
                            <>
                            {t.faces.map(p =>
                                <HTHInteractiveMapMapActivity key={p.id}
                                    handleSelectTerritory={handleSelectTerritory}
                                    polygon={p}
                                    publicMinistryDoc={publicMinistryDoc}
                                    selectionMode={selectionMode}
                                    t={t}
                                />
                            )}
                            </>
                            :
                            <HTHInteractiveMapMapBlock
                                handleSelectTerritory={handleSelectTerritory}
                                selectedTerritories={selectedTerritories}
                                selectionMode={selectionMode}
                                showDates={showDates}
                                t={t}
                                territoryDates={territoryDates}
                            />
                        }
                        {showNumbers &&
                            <HTHInteractiveMapMapNumber
                                t={t}
                            />
                        }
                        {showBuildings &&
                            <>
                            {t.faces.map(p =>
                                <HTHInteractiveMapMapBuildings key={p.id}
                                    polygon={p}
                                />
                            )}
                            </>
                        }
                    </Fragment>
                )}
                {showFamilies && mapDoc.familias.filter(f => f.activo && f.lat && f.lng).map(f =>
                    <Marker key={f.id}
                        options={{
                            icon: {
                                anchor: new google.maps.Point(16, 12),
                                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="red" stroke="white" stroke-width="1" />
                                        <polyline points="9,22 9,12 15,12 15,22" fill="red" />
                                    </svg>
                                `),
                                scaledSize: new google.maps.Size(36, 36)
                            },
                            label: {
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                text: extractInitials(f.familia)
                            }
                        }}
                        onClick={() => setSelectedFamilia(f)}
                        position={{ lat: f.lat!, lng: f.lng! }}
                    />
                )}
                {selectedFamilia &&
                    <InfoWindow
                        onCloseClick={() => setSelectedFamilia(null)}
                        position={{ lat: selectedFamilia.lat!, lng: selectedFamilia.lng! }}
                    >
                        <div>
                            <strong>{selectedFamilia.familia}</strong>
                            <br />
                            {selectedFamilia.direccion}<br />
                            {selectedFamilia.diasHorarios}
                        </div>
                    </InfoWindow>
                }
            </GoogleMap>
        </div>
        </>
    );
}
